import { useEffect, useRef, useState } from "react"

import { Badge, Button, Card, focusOnMount, SettingRow, toast } from "@coval/ui"
import { ChevronRight, Library } from "lucide-react"

import type { AddCatalogItem } from "../hooks/useAddMetricCatalog"

interface AddMetricCardProps {
  addLabel?: string
  isAdded: boolean
  item: AddCatalogItem
  onAdd: (entryId: string, presetKey?: string) => Promise<void> | void
  disabled?: boolean
}

export function AddMetricCard({
  isAdded,
  item,
  onAdd,
  disabled = false,
  addLabel = "Add",
}: AddMetricCardProps) {
  const [isSaving, setIsSaving] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [presetKey, setPresetKey] = useState(item.presets?.default_key)
  const presets = item.presets
  const selectedKey = presetKey ?? presets?.default_key
  const selectedOption = presets?.options.find(
    (option) => option.key === selectedKey,
  )
  const hasValidPreset = !presets || Boolean(selectedOption)
  const pendingRef = useRef(false)

  const mountedRef = useRef(false)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const handleAdd = async () => {
    if (disabled || isAdded || pendingRef.current) {
      return
    }
    pendingRef.current = true
    setIsSaving(true)
    try {
      if (!hasValidPreset) {
        return
      }
      if (presets) {
        await onAdd(item.entry_id, selectedKey)
      } else {
        await onAdd(item.entry_id)
      }
    } catch (error) {
      if (!mountedRef.current) {
        return
      }
      toast.error({
        title: "Error adding metric",
        error,
        fallback: "Unable to add metric.",
      })
    } finally {
      pendingRef.current = false
      if (mountedRef.current) {
        setIsSaving(false)
      }
    }
  }

  return (
    <Card
      className="space-y-3 rounded-md p-3 transition-colors duration-150 hover:bg-surface-default-hover"
      data-testid={`add-metric-card-${item.entry_id}`}
      interactive={Boolean(presets) && !isExpanded && !disabled}
      aria-label={presets && !isExpanded ? item.display_name : undefined}
      aria-expanded={presets && !isExpanded ? false : undefined}
      onClick={presets && !disabled ? () => setIsExpanded(true) : undefined}
    >
      <div className="flex items-start gap-3">
        <Library className="mt-0.5 size-4 shrink-0 text-coval-text-muted" />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium text-coval-text">
              {item.display_name}
            </span>
            {item.status === "inactive" && (
              <Badge
                variant="warning"
                className="h-5 shrink-0 px-1.5 text-[10px]"
              >
                INACTIVE
              </Badge>
            )}
          </div>

          {item.description && (
            <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-coval-text-muted">
              {item.description}
            </p>
          )}

          {item.tags.length > 0 && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1">
              {item.tags.map((tag) => (
                <Badge
                  key={tag}
                  variant="outline"
                  className="h-5 px-1.5 text-[10px]"
                >
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {!presets || isExpanded ? (
          <Button
            variant="secondary"
            className="h-7 shrink-0 px-2.5 text-xs"
            loading={isSaving}
            disabled={disabled || isAdded || isSaving || !hasValidPreset}
            onClick={() => void handleAdd()}
          >
            {isSaving ? "Adding" : isAdded ? "Added" : addLabel}
          </Button>
        ) : (
          <ChevronRight className="mt-0.5 size-4 shrink-0 text-coval-text-muted" />
        )}
      </div>
      {presets && isExpanded && (
        <SettingRow
          label={
            <span className="text-xs font-medium text-coval-text-muted">
              Preset
            </span>
          }
          stacked
        >
          <div
            className="flex flex-wrap gap-2"
            role="group"
            aria-label={`Preset for ${item.display_name}`}
          >
            {presets.options.map((option) => (
              <Button
                key={option.key}
                type="button"
                variant="secondary"
                active={selectedKey === option.key}
                aria-pressed={selectedKey === option.key}
                ref={selectedKey === option.key ? focusOnMount : undefined}
                onClick={() => setPresetKey(option.key)}
                disabled={disabled || isSaving || isAdded}
                className="min-h-7 h-auto whitespace-normal px-2.5 py-1 text-xs"
              >
                {option.label}
              </Button>
            ))}
          </div>
          {selectedOption?.description && (
            <p className="text-xs text-coval-text-muted">
              {selectedOption.description}
            </p>
          )}
          {!hasValidPreset && (
            <p role="alert" className="text-xs text-coval-danger-text">
              Choose an available preset.
            </p>
          )}
        </SettingRow>
      )}
    </Card>
  )
}
